import { MenuRoute } from '@blb-ventures/react-components';
import { useTheme } from '@emotion/react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grow,
  MenuList,
  Paper,
  Popper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { GridFlatList } from '@web/common/components/grid-flat-list';
import { SmartMenu } from '@web/common/components/navigation/smart-menu';
import { coffeeKindLabelDict, coffeeKindToQueryDict } from '@web/store/store-product-options';
import { GridOptionFieldsFragment } from 'core/lib/graphql/store/_gen/grid-options';
import { ProductCoffeeKind } from 'core/lib/graphql/types.d';
import NextLink from 'next/link';
import { FC, MouseEvent, useMemo, useRef, useState } from 'react';

export interface StoreSearchCoffeeFilterProps {
  selectedCoffeeKind?: ProductCoffeeKind;
  selectedRoast: string;
  roastMenu: MenuRoute[];
  roastOptions: GridOptionFieldsFragment[];
  onCoffeeKindChange: (value: ProductCoffeeKind) => void;
  onRoastChange: (roast: string) => void;
  onOpenRoastDrawer: () => void;
}

export const StoreSearchCoffeeFilter: FC<StoreSearchCoffeeFilterProps> = ({
  selectedCoffeeKind,
  selectedRoast,
  roastMenu,
  roastOptions,
  onCoffeeKindChange,
  onRoastChange,
  onOpenRoastDrawer,
}) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const menu = useMemo(
    () =>
      roastMenu.map(it => ({
        ...it,
        onClick: (e: MouseEvent) => {
          if (it.onClick != null) {
            it.onClick(e);
          }
          setOpen(false);
        },
      })),
    [roastMenu],
  );

  const handleToggle = () => setOpen(prevOpen => !prevOpen);

  const handleClose = (event: MouseEvent<Document, MouseEvent>) => {
    if (!anchorRef.current?.contains(event.target as HTMLElement)) {
      setOpen(false);
    }
  };

  const roastButtonProps: Record<string, unknown> = {
    color: selectedRoast ? 'primary' : 'inherit',
    variant: selectedRoast ? 'contained' : 'outlined',
  };

  const selectedRoastLabel = useMemo(() => {
    const foundRoast = roastOptions.flatMap(it => it.values).find(it => it.id === selectedRoast);
    return foundRoast != null ? `: ${foundRoast.name}` : '';
  }, [selectedRoast, roastOptions]);

  return (
    <Box my={2} pt={{ xs: 0, sm: 2 }}>
      <Box pb={1}>
        <Typography variant="subtitle2">Filtro</Typography>
      </Box>
      <Box mb={2}>
        <GridFlatList horizontalBreakpoint="sm">
          {Object.entries(coffeeKindLabelDict).map(([key, value]) => (
            <Box mr={1} key={key}>
              <NextLink
                href={`/loja/categoria/cafes-${coffeeKindToQueryDict[key as ProductCoffeeKind]}`}
                passHref
              >
                <Button
                  onClick={() => onCoffeeKindChange(key as ProductCoffeeKind)}
                  size="small"
                  color={selectedCoffeeKind === key ? 'primary' : 'inherit'}
                  variant={selectedCoffeeKind === key ? 'contained' : 'outlined'}
                >
                  {value}
                </Button>
              </NextLink>
            </Box>
          ))}
          <Box>
            {mdUp ? (
              <>
                <ButtonGroup {...roastButtonProps} ref={anchorRef}>
                  <Button
                    size="small"
                    {...roastButtonProps}
                    onClick={selectedRoast ? () => onRoastChange('') : handleToggle}
                  >
                    Torra{selectedRoastLabel}
                  </Button>
                  <Button {...roastButtonProps} size="small" onClick={handleToggle}>
                    <ArrowDropDownIcon fontSize="small" />
                  </Button>
                </ButtonGroup>
                <Popper
                  open={open}
                  anchorEl={anchorRef.current}
                  role={undefined}
                  transition
                  disablePortal
                  placement="bottom-end"
                >
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      // eslint-disable-next-line react/forbid-component-props
                      style={{
                        transformOrigin: placement === 'bottom' ? 'top center' : 'bottom center',
                      }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={handleClose as any}>
                          <MenuList id="split-button-menu">
                            <SmartMenu itemsOnly menuItems={menu} />
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </>
            ) : (
              <Button size="small" {...roastButtonProps} onClick={onOpenRoastDrawer}>
                Torra{selectedRoastLabel}
              </Button>
            )}
          </Box>
        </GridFlatList>
        <Box mx={-2} my={2}>
          <Divider />
        </Box>
      </Box>
    </Box>
  );
};
